import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useWorkspace } from '../../context/WorkspaceContext';
import { apiFetch } from '../../fetchInterceptor';
import { trackFunnel } from '../../lib/funnelTracker';
import { metaTrack, metaInitiateCheckout } from '../../lib/metaPixel';
import { isSetupGateActive } from '../../lib/setupGate';
import {
  CreditCard, Zap, Check, ArrowRight, Package, BarChart3, AlertCircle,
  Loader2, ExternalLink, Building2, Landmark, Sparkles, HardDrive, ShieldCheck, Plus, ChevronDown
} from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import { Label as UILabel } from '../../components/ui/label';
import ReferralCard from '../ReferralCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CollapsibleSection — slim accordion for the dense Billing page.
 *
 * Wraps blocks the user rarely needs (usage stats, top-up settings,
 * scan history) so the main flow (plans + portal) isn't drowned out.
 * Native <details> would also work but the chevron rotation looks
 * better when we control it ourselves.
 */
function CollapsibleSection({ title, icon: Icon, defaultOpen = false, testId, accent = 'muted', children }) {
  const [open, setOpen] = useState(defaultOpen);
  const accentClass =
    accent === 'amber' ? 'text-amber-500' :
    accent === 'emerald' ? 'text-emerald-500' :
    accent === 'violet' ? 'text-violet-500' :
    'text-muted-foreground';
  return (
    <Card data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-4 flex items-center justify-between gap-3 text-left hover:bg-muted/30 transition-colors rounded-t-xl"
        aria-expanded={open}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-5 h-5 ${accentClass}`} />}
          <span className="font-semibold text-base">{title}</span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-0 border-t" data-testid={testId ? `${testId}-body` : undefined}>
          {children}
        </div>
      )}
    </Card>
  );
}

export const BillingSettings = () => {
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.workspace_id;
  const [credits, setCredits] = useState(null);
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [addons, setAddons] = useState([]);
  const [aiUsage, setAiUsage] = useState(null);
  const [autoTopup, setAutoTopup] = useState({ enabled: false, amount_usd: 10, threshold_pct: 90 });
  const [overage, setOverage] = useState({ enabled: false, pending_usd: 0 });
  const [savingTopup, setSavingTopup] = useState(false);
  const [savingOverage, setSavingOverage] = useState(false);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  // Billing cadence: 'monthly' (default) | 'annual' (15% discount).
  // Initialized from URL (`?cadence=annual`) if landing-page CTA forwarded it.
  const [cadence, setCadence] = useState(() => {
    if (typeof window === 'undefined') return 'monthly';
    const q = new URLSearchParams(window.location.search);
    if (q.get('cadence') === 'annual') return 'annual';
    return 'monthly';
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const aiUsageUrl = workspaceId
        ? `${API_URL}/api/billing/ai-usage?workspace_id=${workspaceId}`
        : `${API_URL}/api/billing/ai-usage`;
      const fetches = [
        apiFetch(`${API_URL}/api/billing/credits`),
        apiFetch(`${API_URL}/api/billing/plans`),
        apiFetch(aiUsageUrl),
        apiFetch(`${API_URL}/api/billing/auto-topup`),
        apiFetch(`${API_URL}/api/billing/overage`),
      ];
      if (workspaceId) {
        fetches.push(apiFetch(`${API_URL}/api/billing/usage/${workspaceId}`));
      }
      const results = await Promise.all(fetches);

      if (results[0].ok) setCredits(await results[0].json());
      if (results[1].ok) {
        const d = await results[1].json();
        setPlans(d.plans || []);
        setPacks(d.credit_packs || []);
        setAddons(d.addons || []);
      }
      if (results[2].ok) setAiUsage(await results[2].json());
      if (results[3].ok) setAutoTopup(await results[3].json());
      if (results[4].ok) setOverage(await results[4].json());
      if (results[5] && results[5].ok) setUsage((await results[5].json()).usage || []);
    } catch (e) {
      console.error('Billing fetch error:', e);
    }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-trigger checkout when the user arrives from the landing-page CTAs.
  // The "plan_intent" is stashed in localStorage by LandingPageDark before
  // login/register, then read here on first dashboard hit. Also supports
  // `?auto=1&plan_intent=X&cadence=Y` for direct deep-links.
  const [autoChecked, setAutoChecked] = useState(false);
  useEffect(() => {
    if (autoChecked) return;
    if (loading) return; // wait for plans/credits to load so handleCheckout is ready
    // Never fire checkout before the user has a workspace — brand-new signups
    // must finish workspace creation first (the intent stays staged until then).
    if (!workspaceId) return;
    // A freshly-created workspace routes through the setup wizard first; the
    // wizard clears the gate and re-triggers checkout when it finishes.
    if (isSetupGateActive()) return;
    let intent = null;
    try {
      const stored = window.localStorage.getItem('plan_intent');
      if (stored) {
        intent = JSON.parse(stored);
        // Treat stale intents (>10 min old) as expired so the user doesn't
        // get bounced to Stripe on a returning visit.
        if (intent && intent.ts && Date.now() - intent.ts > 10 * 60 * 1000) intent = null;
      }
    } catch { /* ignore */ }
    const params = new URLSearchParams(window.location.search);
    const urlPlan = params.get('plan_intent');
    const urlCadence = params.get('cadence');
    if (urlPlan) intent = { plan_id: urlPlan, cadence: urlCadence || 'monthly' };
    if (intent && intent.plan_id) {
      setAutoChecked(true);
      try { window.localStorage.removeItem('plan_intent'); } catch { /* ignore */ }
      // Strip the auto-trigger query params from the URL so a refresh
      // doesn't re-fire the checkout. Was missing before — caused
      // returning users to be bounced to Stripe every time they landed
      // on /settings?tab=billing&auto=1&plan_intent=... (iter302 finding).
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete('auto');
        u.searchParams.delete('plan_intent');
        u.searchParams.delete('cadence');
        window.history.replaceState({}, '', u.pathname + (u.search ? `?${u.searchParams}` : '') + u.hash);
      } catch { /* ignore */ }
      // Guard: if this user already has an active Stripe subscription,
      // do NOT re-launch checkout from a stale intent. Otherwise they
      // get bounced back to Stripe on every login.
      // Also respects the `trial_gate_recently_cancelled` session flag —
      // if the user just bounced back from Stripe (within 15 min) we
      // never want to immediately re-launch checkout for them.
      let recentlyCancelled = 0;
      try {
        recentlyCancelled = parseInt(window.sessionStorage.getItem('trial_gate_recently_cancelled') || '0', 10);
      } catch { /* ignore */ }
      if (recentlyCancelled && Date.now() - recentlyCancelled < 15 * 60 * 1000) {
        return;
      }
      apiFetch(`${API_URL}/api/billing/trial-status`)
        .then((r) => r.ok ? r.json() : null)
        .then((status) => {
          if (status && (status.has_card_on_file ||
                          ['trialing', 'active', 'past_due'].includes(status.status))) {
            // Already subscribed — drop the intent silently. UX: avoid
            // the surprise of redirecting an already-paying customer
            // back to a payment screen.
            return;
          }
          setCadence(intent.cadence === 'annual' ? 'annual' : 'monthly');
          const addons = Array.isArray(intent.addon_ids) ? intent.addon_ids : [];
          setTimeout(() => handleCheckout('plan', intent.plan_id, { cadence: intent.cadence, addon_ids: addons }), 100);
        })
        .catch(() => { /* If the gate is down, don't redirect — safer default */ });
    } else {
      setAutoChecked(true);
    }
     
  }, [loading, autoChecked, workspaceId]);

  // Handle return from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;
    const pollStatus = async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/billing/checkout/status/${sessionId}`);
        if (res.ok) {
          const d = await res.json();
          if (d.payment_status === 'paid') {
            // Funnel: record completion so admin analytics can compute
            // landing → checkout end-to-end conversion.
            trackFunnel('checkout.completed', {
              plan_id: d.item_id, cadence: d.cadence,
            });
            // Meta Purchase — paid upgrade/plan change (user present). Amount
            // and plan resolved server-side from PLANS for the chosen cadence.
            metaTrack('Purchase', { planId: d.item_id, cadence: d.cadence || 'monthly' });
            fetchAll();
            window.history.replaceState({}, '', window.location.pathname + '?tab=billing');
          }
        }
      } catch { /* ignore */ }
    };
    pollStatus();
  }, [fetchAll]);

  // Handle return from the automated win-back save-offer email. The
  // `/api/billing/winback/redeem` endpoint bounces the user here with a
  // `winback=success|already|expired|invalid|error` query param. We toast
  // the outcome and refresh the billing state so the "cancelling" badge
  // flips back to "active" without a hard reload.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('winback');
    if (!status) return;
    if (status === 'success') {
      toast.success("Welcome back! Your 20% off × 3 months has been applied.", { duration: 8000 });
      fetchAll();
    } else if (status === 'already') {
      toast.info("You've already redeemed this save offer — your discount is active.");
    } else if (status === 'expired') {
      toast.error("This save offer has expired. Reply to the email and we'll sort you out.");
    } else if (status === 'invalid') {
      toast.error("This save-offer link is invalid or has been used.");
    } else if (status === 'error') {
      toast.error("Something went wrong applying the offer. Reply to the email so we can fix it.");
    }
    window.history.replaceState({}, '', window.location.pathname + '?tab=billing');
  }, [fetchAll]);

  const handleCheckout = async (itemType, itemId, opts = {}) => {
    setCheckingOut(`${itemType}_${itemId}`);
    const effectiveCadence = itemType === 'plan' ? (opts.cadence || cadence) : 'monthly';
    // Add-ons are only meaningful at PLAN checkout — drop them silently for
    // credit packs / one-time purchases.
    const addonIds = (itemType === 'plan' && Array.isArray(opts.addon_ids)) ? opts.addon_ids : [];
    // Funnel: record the user pressing the checkout button BEFORE we hit
    // Stripe — gives us drop-off attribution between submit & checkout.
    trackFunnel('checkout.initiated', { plan_id: itemId, cadence: effectiveCadence, addon_count: addonIds.length });
    // Meta InitiateCheckout — visitor clicked a plan (top-of-funnel signal).
    // Value = the plan's monthly price, pulled from the loaded plans.
    if (itemType === 'plan') {
      const _p = plans.find((pl) => pl.id === itemId);
      metaInitiateCheckout({ planId: itemId, value: _p?.price, planName: _p?.name });
    }
    try {
      const res = await apiFetch(`${API_URL}/api/billing/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          item_type: itemType,
          item_id: itemId,
          origin_url: window.location.origin,
          workspace_id: workspaceId,
          // Plans support monthly/annual; one-time packs ignore cadence.
          cadence: effectiveCadence,
          addon_ids: addonIds,
        }),
      });

      // Parse defensively — non-JSON responses (e.g., 502 HTML during cold start)
      // would otherwise blow up with "Unexpected token '<'" instead of a real
      // message users can act on.
      const ct = res.headers.get('content-type') || '';
      const parsed = ct.includes('application/json')
        ? await res.json().catch(() => ({}))
        : {};

      if (res.ok && parsed.url) {
        window.location.href = parsed.url;
        return;
      }

      const detail = parsed.detail;
      const msg =
        typeof detail === 'string' ? detail
          : Array.isArray(detail) ? detail.map(e => e.msg).join(', ')
          : res.status === 500 ? 'Payments aren\'t fully set up yet. Please try again shortly or contact support.'
          : res.status === 502 || res.status === 503 ? 'Our server is reconnecting — please try again in a moment.'
          : 'Checkout failed. Please try again.';
      toast.error(msg);
    } catch (e) {
      toast.error(`Checkout failed: ${e.message || 'network error'}`);
    }
    setCheckingOut(null);
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/api/billing/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      if (res.ok) {
        const d = await res.json();
        window.location.href = d.url;
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Could not open subscription portal');
      }
    } catch (e) {
      toast.error('Failed to open portal: ' + e.message);
    }
    setPortalLoading(false);
  };

  const handleBuyAddon = async (addonId) => {
    setCheckingOut(`addon_${addonId}`);
    try {
      const res = await apiFetch(`${API_URL}/api/billing/buy-addon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ addon_id: addonId, origin_url: window.location.origin }),
      });
      if (res.ok) {
        const d = await res.json();
        window.location.href = d.url;
      } else {
        const err = await res.json();
        alert(err.detail || 'Add-on checkout failed');
      }
    } catch (e) {
      alert('Add-on checkout failed: ' + e.message);
    }
    setCheckingOut(null);
  };

  const saveAutoTopup = async (next) => {
    setSavingTopup(true);
    try {
      const res = await apiFetch(`${API_URL}/api/billing/auto-topup`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      if (res.ok) {
        setAutoTopup(await res.json());
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to save auto-top-up');
      }
    } catch (e) {
      alert('Failed to save auto-top-up: ' + e.message);
    }
    setSavingTopup(false);
  };

  const saveOverage = async (enabled) => {
    setSavingOverage(true);
    try {
      const res = await apiFetch(`${API_URL}/api/billing/overage`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (res.ok) {
        setOverage((p) => ({ ...p, enabled }));
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to update overage billing');
      }
    } catch (e) {
      alert('Failed to update overage billing: ' + e.message);
    }
    setSavingOverage(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  const currentPlan = credits?.plan || { id: 'none', name: 'No Plan' };
  const hasActivePlan = currentPlan.id !== 'none';
  // Legacy/grandfathered plans (e.g. 'starter_legacy') resolve to a base tier
  // ('starter') for the plans grid so the customer's current card is marked
  // correctly and they can't accidentally re-checkout at the new price.
  const currentPlanId = credits?.plan_id || currentPlan.id;
  const currentBaseId = String(currentPlanId).replace(/_legacy$/, '');
  const onLegacyRate = /_legacy$/.test(String(currentPlanId));
  const hasStripeCustomer = !!credits?.has_stripe_customer;
  const monthlyCredits = credits?.monthly_credits ?? 0;
  const purchasedCredits = credits?.purchased_credits ?? 0;
  const totalAvailable = credits?.total_available ?? 0;
  const usedThisPeriod = credits?.credits_used_this_period ?? 0;
  const planCreditsTotal = credits?.plan_credits_total ?? 0;
  const plaidUsed = credits?.plaid_accounts_used ?? 0;
  const plaidLimit = credits?.plaid_accounts_limit ?? 0;
  const usedPercent = planCreditsTotal > 0
    ? Math.min(100, Math.round((usedThisPeriod / planCreditsTotal) * 100))
    : 0;

  return (
    <div className="space-y-6" data-testid="billing-settings">
      {/* No active plan warning */}
      {!hasActivePlan && (
        <Card className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="p-5 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-700 dark:text-amber-400">No Active Plan</p>
              <p className="text-sm text-amber-600 dark:text-amber-500 mt-1">
                Subscribe to a plan below to start scanning documents and connecting bank accounts.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── Stripe Customer Portal — always visible when a Stripe customer exists ── */}
      {hasStripeCustomer && (
        <Card
          className="border-emerald-500/30 bg-gradient-to-br from-emerald-50/60 to-white dark:from-emerald-950/30 dark:to-zinc-950"
          data-testid="stripe-portal-card"
        >
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-6 h-6 text-emerald-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-foreground">Stripe Billing Portal</p>
                  <Badge variant="outline" className="text-[10px] font-mono uppercase tracking-wider">
                    Secure · Stripe-hosted
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Update your payment method, download past invoices, change your billing email,
                  cancel or resume your subscription, and manage tax info — all on Stripe&apos;s secure portal.
                </p>
              </div>
              <Button
                onClick={handleManageSubscription}
                disabled={portalLoading}
                data-testid="open-stripe-portal-btn"
                className="gap-2 bg-emerald-500 hover:bg-emerald-600 text-white flex-shrink-0"
              >
                {portalLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4" />
                )}
                Open Portal
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Plan & Manage Subscription */}
      {hasActivePlan && (
        <Card>
          <CardContent className="p-5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <p className="font-semibold text-foreground">{currentPlan.name} Plan</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className="text-sm text-muted-foreground">${currentPlan.price}/month</p>
                  {credits?.subscription_status && (
                    <Badge
                      data-testid="subscription-status-badge"
                      className={
                        credits.subscription_status === 'active' ? 'bg-emerald-500/15 text-emerald-600 text-xs' :
                        credits.subscription_status === 'cancelling' ? 'bg-amber-500/15 text-amber-600 text-xs' :
                        credits.subscription_status === 'past_due' ? 'bg-red-500/15 text-red-600 text-xs' :
                        'bg-muted text-muted-foreground text-xs'
                      }
                    >
                      {credits.subscription_status === 'active' ? 'Active' :
                       credits.subscription_status === 'cancelling' ? 'Cancelling' :
                       credits.subscription_status === 'past_due' ? 'Past Due' :
                       credits.subscription_status}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleManageSubscription}
              disabled={portalLoading}
              data-testid="manage-subscription-btn"
              className="gap-2"
            >
              {portalLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4" />
              )}
              Manage Subscription
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ─── Subscription Plans — promoted to TOP per Feb 30 2026 user feedback ───
          This is THE primary reason users visit Billing. Was previously buried
          mid-page below usage stats. */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <CreditCard className="w-5 h-5" /> Subscription Plans
          </h3>
          {/* Monthly / Annual cadence toggle */}
          <div className="relative inline-flex p-1 rounded-full border border-border bg-muted" data-testid="billing-cadence-toggle">
            <span
              aria-hidden
              className={`absolute top-1 bottom-1 w-[112px] rounded-full bg-emerald-500 transition-transform duration-300 ease-out ${cadence === 'monthly' ? 'translate-x-0' : 'translate-x-[112px]'}`}
              style={{ left: 4 }}
            />
            <button
              type="button"
              onClick={() => setCadence('monthly')}
              data-testid="cadence-monthly"
              className={`relative z-10 w-[112px] py-1.5 text-sm font-semibold rounded-full transition-colors ${cadence === 'monthly' ? 'text-black' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setCadence('annual')}
              data-testid="cadence-annual"
              className={`relative z-10 w-[112px] py-1.5 text-sm font-semibold rounded-full transition-colors flex items-center justify-center gap-1.5 ${cadence === 'annual' ? 'text-black' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Annual
              <span className={`text-[10px] font-mono uppercase tracking-wider px-1 py-0.5 rounded ${cadence === 'annual' ? 'bg-black/20 text-black' : 'bg-emerald-500/15 text-emerald-600'}`}>Save up to 2 mo</span>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="plans-grid">
          {plans.map((plan) => {
            const isCurrent = currentPlan.id === plan.id || currentBaseId === plan.id;
            // The customer holds a grandfathered rate for THIS tier.
            const isLegacyHere = onLegacyRate && currentBaseId === plan.id;
            const isPopular = plan.id === 'pro';
            // Prefer backend-computed annual figures (per-plan discount) and
            // fall back to the legacy 2-months-free math for older responses.
            const annualMonthly = plan.annual_monthly_usd ?? (plan.price > 0 ? +(plan.price * 0.8333).toFixed(2) : 0);
            // Show the grandfathered customer THEIR actual rate on their card.
            const displayPrice = isLegacyHere
              ? Number(currentPlan.price)
              : (cadence === 'annual' ? annualMonthly : plan.price);
            const annualTotal = plan.annual_price_usd ?? (plan.price > 0 ? Math.round(plan.price * 12 * 0.8333) : 0);
            const discountPct = plan.annual_discount_pct != null ? Math.round(plan.annual_discount_pct * 100) : null;
            return (
              <Card
                key={plan.id}
                className={`relative ${isCurrent ? 'border-emerald-500 border-2' : ''} ${isPopular ? 'ring-2 ring-emerald-500/30' : ''}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {isPopular && !isCurrent && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <Badge className="bg-emerald-600 text-white text-[10px] shadow-sm">Most Popular</Badge>
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3 right-3 z-10">
                    <Badge className="bg-white dark:bg-neutral-900 border border-emerald-500 text-emerald-600 hover:bg-white dark:hover:bg-neutral-900 text-[10px] shadow-sm">
                      {isLegacyHere ? 'Current · Legacy rate' : 'Current'}
                    </Badge>
                  </div>
                )}
                <CardContent className="p-4 flex flex-col h-full">
                  <h4 className="font-semibold text-base">{plan.name}</h4>
                  <div className="mt-2">
                    <span className="text-2xl font-bold" data-testid={`plan-amount-${plan.id}`}>${displayPrice?.toFixed(2)}</span>
                    <span className="text-sm text-muted-foreground">/mo</span>
                  </div>
                  <div className="h-4">
                    {isLegacyHere ? (
                      <p className="text-[10px] font-mono text-emerald-600 uppercase tracking-wider" data-testid={`plan-legacy-${plan.id}`}>
                        Grandfathered — new price ${plan.price?.toFixed(2)}/mo
                      </p>
                    ) : cadence === 'annual' && plan.price > 0 ? (
                      <p className="text-[10px] font-mono text-emerald-600 uppercase tracking-wider" data-testid={`plan-annual-${plan.id}`}>
                        ${annualTotal.toFixed(2)}/yr{discountPct ? ` · save ${discountPct}%` : ''}
                      </p>
                    ) : null}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{plan.description}</p>
                  <ul className="mt-3 space-y-1.5 flex-1">
                    {(plan.features || []).map((f, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                        <Check className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4">
                    {isCurrent ? (
                      <Button variant="outline" size="sm" className="w-full" disabled data-testid={`plan-current-${plan.id}`}>
                        {isLegacyHere ? 'Your plan · $' + Number(currentPlan.price).toFixed(2) + '/mo' : 'Current Plan'}
                      </Button>
                    ) : plan.id === 'free' ? (
                      <Button variant="outline" size="sm" className="w-full" disabled data-testid={`plan-free-${plan.id}`}>
                        Free — auto-active
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => handleCheckout('plan', plan.id)}
                        disabled={!!checkingOut}
                        data-testid={`checkout-plan-${plan.id}`}
                      >
                        {checkingOut === `plan_${plan.id}` ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        ) : (
                          <ArrowRight className="w-4 h-4 mr-1" />
                        )}
                        {hasActivePlan ? (currentPlan.price < plan.price ? 'Upgrade' : 'Switch') : 'Subscribe'}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Usage / credit / top-up details all live at the bottom of this
          page now (in the "Usage & Limits" section). Per Feb 30 2026
          feedback, casual visitors should see plans + manage-subscription
          first, not the dense numbers. */}

      {/* Referral program — both get $10 of premium AI */}
      <ReferralCard />

      {/* Add-ons */}
      {addons.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5" /> Add-ons
            <span className="text-sm font-normal text-muted-foreground">— stack on any plan</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="addons-grid">
            {addons.map((a) => (
              <Card key={a.id} className="hover:border-emerald-500/50 transition-colors">
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-sm">{a.label}</p>
                    <p className="text-xs text-muted-foreground capitalize">{a.type === 'recurring' ? 'Monthly' : 'One-time'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold">${a.price?.toFixed(2)}</span>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => handleBuyAddon(a.id)}
                      disabled={!!checkingOut}
                      data-testid={`buy-addon-${a.id}`}
                    >
                      {checkingOut === `addon_${a.id}` ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>Buy <ExternalLink className="w-3 h-3 ml-1" /></>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Credit Packs */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Package className="w-5 h-5" /> Credit Packs
          <span className="text-sm font-normal text-muted-foreground">— one-time purchase, rolls over</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {packs.map((pack) => (
            <Card key={pack.id} className="hover:border-emerald-500/50 transition-colors">
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="font-semibold">{pack.label}</p>
                  <p className="text-sm text-muted-foreground">${(pack.price / pack.credits).toFixed(2)}/page</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xl font-bold">${pack.price}</span>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => handleCheckout('credit_pack', pack.id)}
                    disabled={!!checkingOut}
                    data-testid={`checkout-pack-${pack.id}`}
                  >
                    {checkingOut === `credit_pack_${pack.id}` ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>Buy <ExternalLink className="w-3 h-3 ml-1" /></>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════
          Usage & Limits — single collapsed section at the bottom holding
          every "nice to know but rarely actionable" metric. Users who
          want to peek at credit consumption, AI spend, top-up settings,
          or scan history expand the relevant accordion. Default state
          is fully collapsed so the Billing page lands on plans + manage.
          ═══════════════════════════════════════════════════════════════ */}
      <div className="pt-2">
        <h3 className="text-base font-semibold text-muted-foreground mb-3 flex items-center gap-2">
          <BarChart3 className="w-4 h-4" /> Usage &amp; Limits
          <span className="text-xs font-normal italic">— optional details</span>
        </h3>
        <div className="space-y-3">
          {/* Credits & Usage */}
          <CollapsibleSection title="Credits &amp; Usage" icon={Zap} accent="amber" testId="usage-credits-section">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4">
              <div className="text-center p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                <p className="text-3xl font-bold text-emerald-600" data-testid="total-credits">{totalAvailable}</p>
                <p className="text-sm text-muted-foreground mt-1">Total Available</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted border">
                <p className="text-3xl font-bold text-foreground" data-testid="monthly-credits">{monthlyCredits}</p>
                <p className="text-sm text-muted-foreground mt-1">Monthly Credits</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">Resets each period</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800">
                <p className="text-3xl font-bold text-violet-600" data-testid="purchased-credits">{purchasedCredits}</p>
                <p className="text-sm text-muted-foreground mt-1">Purchased Credits</p>
                <p className="text-[10px] text-violet-400 mt-0.5">Rolls over</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted border">
                <p className="text-3xl font-bold text-foreground">{usedThisPeriod}</p>
                <p className="text-sm text-muted-foreground mt-1">Used This Period</p>
              </div>
            </div>
            {planCreditsTotal > 0 && (
              <div className="mt-4">
                <div className="flex justify-between text-sm text-muted-foreground mb-1">
                  <span>Monthly usage: {usedThisPeriod} / {planCreditsTotal}</span>
                  <span>{usedPercent}%</span>
                </div>
                <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${usedPercent > 80 ? 'bg-red-500' : usedPercent > 50 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                    style={{ width: `${usedPercent}%` }}
                  />
                </div>
              </div>
            )}
            {hasActivePlan && (
              <div className="mt-4 flex items-center gap-3 px-3 py-2 rounded-lg bg-muted border">
                <Landmark className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Bank Connections: <span className="font-semibold text-foreground" data-testid="plaid-usage">{plaidUsed} / {plaidLimit}</span>
                </span>
                {plaidUsed >= plaidLimit && plaidLimit > 0 && (
                  <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-600 ml-auto">Limit reached</Badge>
                )}
              </div>
            )}
          </CollapsibleSection>

          {/* AI Usage This Month */}
          {aiUsage && (
            <CollapsibleSection title="AI Usage This Month" icon={Sparkles} accent="emerald" testId="usage-ai-section">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
                <div className="rounded-lg border p-3">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Premium AI Spend</p>
                  <p className="text-2xl font-bold mt-1" data-testid="ai-spend-mtd">
                    ${aiUsage.ai_spend_mtd_usd?.toFixed(2)} <span className="text-sm font-normal text-muted-foreground">/ ${aiUsage.ai_monthly_cap_usd?.toFixed(2)}</span>
                  </p>
                  <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden mt-2">
                    <div
                      className={`h-full rounded-full transition-all ${aiUsage.ai_status === 'capped' ? 'bg-violet-500' : aiUsage.ai_status === 'warn' ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${Math.min(100, aiUsage.ai_pct_used)}%` }}
                    />
                  </div>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> Free Llama Questions
                  </p>
                  <p className="text-2xl font-bold mt-1" data-testid="free-ai-today">
                    {aiUsage.free_ai_used_today} <span className="text-sm font-normal text-muted-foreground">/ {aiUsage.free_ai_daily_cap > 900 ? 'Unlimited' : `${aiUsage.free_ai_daily_cap}/day`}</span>
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-2">Always free — never blocks you</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                    <HardDrive className="w-3 h-3" /> Storage
                  </p>
                  <p className="text-2xl font-bold mt-1" data-testid="storage-used">
                    {aiUsage.storage_used_gb} <span className="text-sm font-normal text-muted-foreground">/ {aiUsage.storage_limit_gb} GB</span>
                  </p>
                  {aiUsage.storage_limit_gb > 0 && (
                    <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden mt-2">
                      <div
                        className={`h-full rounded-full ${aiUsage.storage_pct > 80 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                        style={{ width: `${Math.min(100, aiUsage.storage_pct)}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div
                className="rounded-lg border border-violet-500/30 bg-violet-500/5 text-violet-700 dark:text-violet-300 px-4 py-3 text-sm flex items-start gap-2 mt-4"
                data-testid="free-fallback-callout"
              >
                <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>You&apos;ll never be blocked.</strong> If you exceed your premium AI cap, CountBucks automatically falls back to the free Llama 3.3 model so your team keeps working.
                </span>
              </div>
            </CollapsibleSection>
          )}

          {/* Auto Top-up + Overage — combined */}
          {aiUsage && aiUsage.plan_id !== 'free' && (
            <CollapsibleSection title="Auto top-up &amp; pay-as-you-go" icon={Zap} accent="emerald" testId="usage-topup-section">
              <div className="space-y-3 pt-4">
                <div className="rounded-lg border p-4" data-testid="auto-topup-card">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                        <Zap className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div>
                        <p className="font-semibold">Auto top-up premium AI</p>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          When you hit {autoTopup.threshold_pct}% of your premium cap, we&apos;ll automatically charge ${autoTopup.amount_usd?.toFixed?.(2) || autoTopup.amount_usd} for an instant top-up — so you never get bounced to the free model.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {savingTopup && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                      <Switch
                        data-testid="auto-topup-toggle"
                        checked={!!autoTopup.enabled}
                        onCheckedChange={(checked) => saveAutoTopup({ ...autoTopup, enabled: checked })}
                      />
                    </div>
                  </div>
                  {autoTopup.enabled && (
                    <div className="flex flex-wrap items-center gap-3 text-sm pt-4 border-t mt-4">
                      <UILabel htmlFor="topup-amount" className="text-muted-foreground">Top-up amount</UILabel>
                      <select
                        id="topup-amount"
                        className="h-9 rounded border bg-background px-2"
                        value={autoTopup.amount_usd}
                        onChange={(e) => saveAutoTopup({ ...autoTopup, amount_usd: parseFloat(e.target.value) })}
                        data-testid="auto-topup-amount"
                      >
                        {[10, 25, 50, 100].map(v => (
                          <option key={v} value={v}>${v}</option>
                        ))}
                      </select>
                      <UILabel className="text-muted-foreground ml-4">Trigger at</UILabel>
                      <select
                        className="h-9 rounded border bg-background px-2"
                        value={autoTopup.threshold_pct}
                        onChange={(e) => saveAutoTopup({ ...autoTopup, threshold_pct: parseInt(e.target.value, 10) })}
                        data-testid="auto-topup-threshold"
                      >
                        {[80, 90, 95, 100].map(v => (
                          <option key={v} value={v}>{v}%</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="rounded-lg border p-4 flex items-center justify-between gap-4" data-testid="overage-card">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center flex-shrink-0">
                      <BarChart3 className="w-5 h-5 text-violet-500" />
                    </div>
                    <div>
                      <p className="font-semibold">Pay-as-you-go overage</p>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        Instead of chunky top-ups, every $5 of premium AI over your cap is added to your next Stripe invoice. Smoother, predictable.
                        {overage.pending_usd > 0 && (
                          <span className="ml-2 text-violet-500 font-medium">
                            ${overage.pending_usd.toFixed(2)} pending
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {savingOverage && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                    <Switch
                      data-testid="overage-toggle"
                      checked={!!overage.enabled}
                      onCheckedChange={(checked) => saveOverage(checked)}
                    />
                  </div>
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Recent Scan Usage */}
          <CollapsibleSection title="Recent Scan Usage" icon={BarChart3} testId="usage-scans-section">
            <div className="pt-4">
              {usage.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No scans yet. Upload a document to get started.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {usage.map((u, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 rounded bg-muted text-sm">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-[10px]">{u.doc_type?.replace('_', ' ')}</Badge>
                        <span className="text-muted-foreground">{u.job_id}</span>
                      </div>
                      <div className="flex items-center gap-4 text-muted-foreground">
                        <span>{u.pages_scanned} page{u.pages_scanned !== 1 ? 's' : ''}</span>
                        <span>{new Date(u.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CollapsibleSection>
        </div>
      </div>
    </div>
  );
};
